import React from "react";
import {
  Grid,
  Box,
  Paper,
  Switch,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Select,
  InputLabel,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  TextField,
  Radio,
  RadioGroup,
  Menu,
} from "@mui/material";
import request from "../../../utilities/requests";
import { EventContext } from "../../../contexts/EventContext";
import { MatchContext } from "../../../contexts/MatchContext";
import { toast } from "../../../utilities/SnackBarUtils";

const pairingTypeHelp = {
  hetero: "Pairs will be made between He/Him and She/Her only",
  menseekingmen:
    "It is assumed that all attendees identify as He/Him and will be matched with each other",
  lesbian:
    "It is assumed that all attendees identify as She/Her and will be matched with each other",
  lgbtq:
    "Attendees may choose their pronouns and choose who they are interested in meeting",
  friend: "Pronouns are not considered",
  openspectrum: "Pronouns are not considered",
  dinner: "A hostless mixer-like event. Attendees can mark themselves as present. Checking in is not required. Attendees will see the Guest List"
};

const QuestionGroupSelect = ({ selectedGroup, questionGroups, onSelect }) => (
  <FormControl fullWidth variant="outlined" sx={{ width: 250, mb: 2 }}>
    <InputLabel sx={{ top: -5 }}>Question Groups</InputLabel>
    <Select
      value={selectedGroup}
      label="Question Groups"
      onChange={(e) => onSelect(e.target.value)}
      sx={{ height: 45 }}
    >
      {questionGroups.map((option) => (
        <MenuItem key={option._id} value={option._id}>
          {option.name}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const PairingEventTypeSelect = ({ pairingEventType, onChange }) => (
  <FormControl fullWidth variant="outlined" sx={{ width: 250, mb: 2 }}>
    <InputLabel sx={{ top: -5 }}>Pairing Event Type</InputLabel>
    <Select
      value={pairingEventType || ""}
      label="Pairing Event Type"
      onChange={(e) => onChange(e.target.value)}
      sx={{ height: 45 }}
    >
      <MenuItem value="hetero">Hetero Speed Dating</MenuItem>
      <MenuItem value="menseekingmen">Men Seeking Men Speed Dating</MenuItem>
      <MenuItem value="lesbian">Lesbian Speed DAting</MenuItem>
      <MenuItem value="lgbtq">LGBTQ Speed Dating</MenuItem>
      <MenuItem value="friend">Speed Friending</MenuItem>
      <MenuItem value="openspectrum">Open Spectrum Speed Dating</MenuItem>
      <MenuItem value="mixer">Mixer</MenuItem>
      <MenuItem value="group">Group</MenuItem>
      <MenuItem value="dinner">Dinner</MenuItem>
    </Select>
    <Typography variant="caption" sx={{ p: 1 }}>
      {pairingTypeHelp[pairingEventType] || ""}
    </Typography>
  </FormControl>
);

const MixerGroupingSelect = ({
  showGrouping,
  selectedGrouping,
  groupings,
  onChange,
}) => {
  if (!showGrouping) return null;
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        width: 250,
        mb: 2,
      }}
    >
      <FormControl fullWidth variant="outlined">
        <InputLabel sx={{ top: -5 }}>Filter</InputLabel>
        <Select
          value={selectedGrouping}
          label="Mixer Filter"
          onChange={(e) => onChange(e.target.value)}
          sx={{ height: 45 }}
        >
          <MenuItem value="none">None</MenuItem>
          {groupings.map((option) => (
            <MenuItem key={option._id} value={option._id}>
              {option.systemLabel}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Typography variant="caption" sx={{ pl: 1 }}>
        The Mixer Filter acts as a filter on which attendees a user can see. It
        must be a 'related' question.
      </Typography>
    </Box>
  );
};

const SettingsSwitches = ({ settings, onSwitchChange, pairingEvent }) => (
  <Box sx={{ width: "100%", mb: 2 }}>
    <FormControl variant="standard">
      <FormGroup>
        <FormControlLabel
          control={
            <Switch
              checked={settings.photoRequired}
              onChange={() =>
                onSwitchChange("photoRequired", !settings.photoRequired)
              }
            />
          }
          label="Profile Photo Required"
        />
        <FormControlLabel
          control={
            <Switch
              checked={settings.showGuestList}
              onChange={() =>
                onSwitchChange("showGuestList", !settings.showGuestList)
              }
            />
          }
          label="Show Guestlist"
        />
        <FormControlLabel
          control={
            <Switch
              checked={settings.showNotebook}
              onChange={() =>
                onSwitchChange("showNotebook", !settings.showNotebook)
              }
            />
          }
          label="Show Notebook"
        />
        {pairingEvent?.pairingEventType === "group" && (
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.autoFillGroups}
                  onChange={() =>
                    onSwitchChange("autoFillGroups", !settings.autoFillGroups)
                  }
                />
              }
              label="Auto Fill Groups"
            />
            <Typography variant="caption">
              Any attendees not assigned to a group will be randomly assigned
              (groups may have odd numbers)
            </Typography>
          </>
        )}
      </FormGroup>
    </FormControl>
  </Box>
);

const DisplayEvent = ({ settings, onSwitchChange, pairingEvent }) => (
  <Box sx={{ width: "100%", mb: 2 }}>
    <FormControl variant="standard">
      <FormGroup>
        <FormControlLabel
          sx={{ mb: 2 }}
          control={
            <Switch
              checked={settings.display}
              onChange={() => onSwitchChange("display", !settings.display)}
            />
          }
          label="Display Event"
        />
        <Typography variant="caption" style={{ marginTop: -15 }}>
          If toggled on, then users will be able to see the event in the Home
          tab of the user app
        </Typography>
      </FormGroup>
    </FormControl>
  </Box>
);

const TrackIsPresent = ({ settings, onSwitchChange, pairingEvent }) => (
  <Box sx={{ width: "100%", mb: 2 }}>
    <FormControl variant="standard">
      <FormGroup>
        <FormControlLabel
          sx={{ mb: 2 }}
          control={
            <Switch
              checked={
                pairingEvent?.pairingEventType === "dinner"
                  ? true
                  : settings.trackIsPresent
              }
              disabled={pairingEvent?.pairingEventType === "dinner"}
              onChange={() =>
                onSwitchChange("trackIsPresent", !settings.trackIsPresent)
              }
            />
          }
          label="Track Attendee Presence"
        />
        <Typography variant="caption" style={{ marginTop: -15 }}>
          If toggled on, you will see if an attendees ticket has been scanned
          and you will be able to manually mark whether an attendee is present
          at the event.
        </Typography>
      </FormGroup>
    </FormControl>
  </Box>
);

const AutoComplete = ({ settings, onSwitchChange, pairingEvent }) => (
  <Box sx={{ width: "100%", mb: 2 }}>
    <FormControl variant="standard">
      <FormGroup>
        <FormControlLabel
          sx={{ mb: 2 }}
          control={
            <Switch
              checked={settings.autoComplete}
              onChange={() =>
                onSwitchChange("autoComplete", !settings.autoComplete)
              }
            />
          }
          label="Auto Complete Event"
        />
        {settings?.autoComplete ? (
          <>
            <FormControl component="fieldset" fullWidth sx={{ mb: 1 }}>
              <RadioGroup
                row
                value={settings.autoCompleteMode}
                onChange={(e) =>
                  onSwitchChange("autoCompleteMode", e.target.value)
                }
              >
                <FormControlLabel
                  value="minutes"
                  control={<Radio />}
                  label="After minutes"
                />
                <FormControlLabel
                  value="time"
                  control={<Radio />}
                  label="At specific time"
                />
              </RadioGroup>
            </FormControl>
            {settings.autoCompleteMode === "minutes" ? (
              <TextField
                type="number"
                inputProps={{
                  min: 1,
                  step: 1,
                  pattern: "[0-9]*",
                  inputMode: "numeric",
                }}
                label="Auto Complete After (minutes)"
                value={settings.autoCompleteAfter}
                onChange={(e) => {
                  const val = e.target.value;
                  if (/^\d*$/.test(val)) {
                    onSwitchChange(
                      "autoCompleteAfter",
                      val === "" ? "" : parseInt(val, 10)
                    );
                  }
                }}
                variant="outlined"
                size="small"
                sx={{
                  mb: 1.5,
                  mt: 0.5,
                  maxWidth: 220,
                  "& .MuiInputBase-root": { background: "#f9fafc" },
                }}
                fullWidth
                placeholder="e.g. 120"
                helperText="The event will be completed this many minutes once the last round has started"
              />
            ) : (
              <TextField
                type="time"
                label="Complete At"
                value={settings.autoCompleteTime}
                onChange={(e) =>
                  onSwitchChange("autoCompleteTime", e.target.value)
                }
                variant="outlined"
                size="small"
                sx={{ mb: 1.5, mt: 0.5, maxWidth: 220 }}
                fullWidth
                InputLabelProps={{ shrink: true }}
                helperText="Select clock time to complete the event"
              />
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={settings.autoSendMatches}
                  onChange={() =>
                    onSwitchChange("autoSendMatches", !settings.autoSendMatches)
                  }
                />
              }
              label="Auto Send Matches"
            />
            <Typography variant="caption" sx={{ pl: 1 }}>
              When enabled, all matches will be automatically emailed to
              attendees after the event is completed.
            </Typography>
          </>
        ) : null}
      </FormGroup>
    </FormControl>
  </Box>
);

const ConfirmDialog = ({ open, onClose, onConfirm }) => (
  <Dialog open={open} onClose={onClose}>
    <DialogTitle>Wait! Are you sure?</DialogTitle>
    <DialogContent>
      <DialogContentText>
        Are you sure you want to assign a different question group to this
        event? This will require users to fill out a different profile for this
        event. If they have already filled out the old one, they will now need
        to fill out the new one...they might get grumpy!
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button autoFocus onClick={onConfirm}>
        Confirm
      </Button>
    </DialogActions>
  </Dialog>
);

const Settings = () => {
  const { pairingEvent, setPairingEvent, userData } =
    React.useContext(EventContext);
  const { warnings, setWarnings } = React.useContext(MatchContext);
  const [settings, setSettings] = React.useState({
    photoRequired: false,
    showGuestList: false,
    autoFillGroups: false,
    showNotebook: false,
    autoComplete: false,
    autoCompleteAfter: "",
    autoCompleteMode: "minutes",
    autoCompleteTime: "",
    autoSendMatches: false,
    display: false,
    breakAfter: 0,
  });
  const [questionGroups, setQuestionGroups] = React.useState([]);
  const [selectedGroup, setSelectedGroup] = React.useState("");
  const newGroup = React.useRef();
  const [pairingEventType, setPairingEventType] = React.useState("");
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [showGrouping, setShowGrouping] = React.useState(false);
  const [groupings, setGroupings] = React.useState([]);
  const [selectedGrouping, setSelectedGrouping] = React.useState("null");

  React.useEffect(() => {
    if (pairingEvent) {
      getQuestionGroups();
      setSettings({
        photoRequired: pairingEvent?.event?.photoRequired,
        showGuestList: pairingEvent?.event?.showGuestList,
        autoFillGroups: pairingEvent?.event?.autoFillGroups,
        showNotebook: pairingEvent?.event?.showNotebook,
        autoComplete: pairingEvent?.event?.autoComplete,
        autoCompleteAfter: pairingEvent?.event?.autoCompleteAfter || "",
        autoSendMatches: pairingEvent?.event?.autoSendMatches,
        autoCompleteMode: pairingEvent.event.autoCompleteMode || "time",
        autoCompleteTime: pairingEvent.event.autoCompleteTime || "",
        display: pairingEvent?.event?.display,
        trackIsPresent: pairingEvent?.event?.trackIsPresent,
        breakAfter: pairingEvent?.event?.breakAfter || 0,
      });
      setPairingEventType(pairingEvent?.pairingEventType);
      setSelectedGrouping(
        pairingEvent?.mixerGrouping === null
          ? "none"
          : pairingEvent?.mixerGrouping
      );
    }
  }, []);

  React.useEffect(() => {
    if (["mixer", "group"].includes(pairingEventType)) {
      setShowGrouping(true);
      if (groupings.length === 0) {
        getMixerGroupings();
      }
    } else {
      setShowGrouping(false);
    }
  }, [pairingEventType]);

  const getQuestionGroups = async () => {
    const result = await request(
      `questiongroups?orgId=${pairingEvent?.event?.org}`
    );
    if (result.success) {
      setQuestionGroups(result.questionGroups);
      const eResult = await request(
        `questiongroups/eventquestiongroup?eventId=${pairingEvent?.event?._id}`
      );
      if (eResult.success && eResult?.eventQuestionGroup?.questionGroup) {
        setSelectedGroup(eResult.eventQuestionGroup.questionGroup);
        setWarnings(warnings.filter((w) => w.type !== "questionGroup"));
      }
    }
  };

  const getMixerGroupings = async () => {
    const result = await request(
      `pairingevent/mixergroupings?eventId=${pairingEvent.event._id}`
    );
    if (result.success) {
      const g = result.groupings.filter((i) => i.questionType === "related");
      setGroupings(g);
    }
  };

  const handleConfirm = (questionGroupId) => {
    newGroup.current = questionGroupId;
    if (selectedGroup === "") {
      updateEventQuestionGroup();
      return;
    }
    setConfirmOpen(true);
  };

  const updateEventQuestionGroup = async () => {
    const body = {
      eventId: pairingEvent?.event?._id,
      questionGroupId: newGroup.current,
    };
    const result = await request("questiongroups/assign", "post", body);
    if (result.success) {
      setSelectedGroup(newGroup.current);
      setWarnings(warnings.filter((w) => w.type !== "questionGroup"));
      toast.success("Question group assigned to event successfully!");
      const result = await request(
        `pairingevent?eventId=${pairingEvent.event._id}`
      );
      if (result.success) {
        setPairingEvent(result.pairingEvents);
      }
    }
    setConfirmOpen(false);
  };

  const handleChange = async (field, value) => {
    // Optimistically update state immediately
    setSettings((prev) => ({ ...prev, [field]: value }));

    try {
      const body = {
        eventId: pairingEvent.event._id,
        update: { [field]: value },
      };
      const result = await request("events/update", "post", body);
      if (!result.success) {
        toast.error(`Error updating event details: ${result.error}`);
        setSettings((prev) => ({
          ...prev,
          [field]: pairingEvent.event[field],
        }));
      } else {
        toast.success("Setting updated!");
      }
    } catch (e) {
      console.error(`Error updating event details: ${e.message}`);
      toast.error(`Network error updating event details`);
      setSettings((prev) => ({ ...prev, [field]: pairingEvent.event[field] }));
    }
  };

  const handlePairingEventTypeChange = async (val) => {
    const body = {
      pairingEventId: pairingEvent._id,
      update: { pairingEventType: val },
    };
    try {
      const result = await request("pairingevent/update", "post", body);
      if (result.success) {
        setPairingEventType(val);
        setWarnings(warnings.filter((w) => w.type !== "pairingEventType"));
        if (val === "dinner") {
          handleChange("trackIsPresent", true);
        } else if (val === "group") {
          handleChange("autoFillGroups", true)
        }
        toast.success("Pairing event type updated successfully");
        const result = await request(
          `pairingevent?eventId=${pairingEvent.event._id}`
        );
        if (result.success) {
          setPairingEvent(result.pairingEvents);
        }
      }
    } catch (e) {
      toast.error(`Error updating pairing event type. Error: ${e.message}`);
    }
  };

  const handleGroupingChange = async (val) => {
    const body = {
      eventId: pairingEvent.event._id,
      questionId: val,
    };
    const result = await request("pairingevent/setmixergrouping", "post", body);
    if (result.success) {
      setSelectedGrouping(val);
      toast.success("Mixer grouping updated");
    }
  };

  const BreakAfterSelect = () => {
    if (['mixer','dinner'].includes(pairingEvent?.pairingEventType)) return null;
    const rounds = [];
    for (let i = 1; i <= pairingEvent.numberOfRounds; i++) {
      rounds.push(i);
    }

    return (
      <FormControl fullWidth variant="outlined" sx={{ width: 250, mb: 2 }}>
        <InputLabel sx={{ top: -5 }}>Break After Round</InputLabel>
        <Select
          value={settings.breakAfter}
          label="Break After"
          onChange={(e) => handleChange("breakAfter", e.target.value)}
          sx={{ height: 45 }}
        >
          <MenuItem value={0}>None</MenuItem>
          {rounds.map((r) => {
            return (
              <MenuItem key={`round-${r}`} value={r}>
                {r}
              </MenuItem>
            );
          })}
        </Select>
        <Typography variant="caption" sx={{ p: 1 }}>
          Choose the round after which you would like a break. Attendees will
          see a message that everyone is on a break.
        </Typography>
      </FormControl>
    );
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <QuestionGroupSelect
            selectedGroup={selectedGroup}
            questionGroups={questionGroups}
            onSelect={handleConfirm}
          />
        </Grid>
        <Grid item xs={12}>
          <PairingEventTypeSelect
            pairingEventType={pairingEventType}
            onChange={handlePairingEventTypeChange}
          />
        </Grid>
        <Grid item xs={12}>
          <MixerGroupingSelect
            showGrouping={showGrouping}
            selectedGrouping={selectedGrouping}
            groupings={groupings}
            onChange={handleGroupingChange}
          />
        </Grid>
        <Grid item xs={12}>
          <BreakAfterSelect />
        </Grid>
        <Grid item md={6} xs={12}>
          <SettingsSwitches
            settings={settings}
            onSwitchChange={handleChange}
            pairingEvent={pairingEvent}
          />
        </Grid>
        <Grid item md={6} xs={12}>
          <DisplayEvent
            settings={settings}
            onSwitchChange={handleChange}
            pairingEvent={pairingEvent}
          />
          <TrackIsPresent
            settings={settings}
            onSwitchChange={handleChange}
            pairingEvent={pairingEvent}
          />
          <AutoComplete
            settings={settings}
            onSwitchChange={handleChange}
            pairingEvent={pairingEvent}
          />
        </Grid>
      </Grid>
      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={updateEventQuestionGroup}
      />
      {userData?.role === "superadmin" ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Typography variant="caption" style={{ color: "grey" }}>
            Event ID: {pairingEvent?.event?._id}
          </Typography>
          <Select
            value={pairingEvent?.event?.invoiceStatus || "none"}
            size="small"
            style={{ width: 200 }}
            onChange={async (e) => {
              const body = {
                eventId: pairingEvent.event._id,
                update: { invoiceStatus: e.target.value },
              };
              const result = await request("events/update", "post", body);
              if (result.success) {
                toast.success(
                  "Invoice status update successfully. Please refresh to see changes."
                );
              }
            }}
          >
            <MenuItem value="none">None</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="notChargeable">Not Chargeable</MenuItem>
          </Select>
        </div>
      ) : null}
    </Paper>
  );
};

export default Settings;
